export interface ListTriggersRequest {}

export interface ListTriggersResponse {
  /** Custom triggers. */
  customTriggers?: ListTriggersResponseCustomTrigger[];
}

export interface ListTriggersResponseCustomTrigger {
  /**
   * Custom trigger ID.
   * @minLength 1
   * @maxLength 100
   */
  _id?: string;
  /**
   * Custom trigger name to display in the dashboard.
   * @minLength 1
   * @maxLength 200
   */
  name?: string;
}

export interface GetEligibleTriggersRequest {
  /**
   * List of line items in the cart/checkout.
   * @maxSize 1000
   */
  lineItems?: LineItem[];
  /**
   * List of triggers to be checked for discount eligibility.
   * @minSize 1
   * @maxSize 1000
   */
  triggers?: TriggerToFilterBy[];
  /**
   * Persistent ID that correlates between the various eCommerce elements: cart, checkout, and order.
   * @format GUID
   */
  purchaseFlowId?: string | null;
  /** Shipping information. */
  shippingInfo?: ShippingInfo;
}

export interface LineItem {
  /**
   * Line item ID.
   * @minLength 1
   * @maxLength 100
   */
  _id?: string;
  /**
   * Item quantity in this line item.
   * @max 100000
   */
  quantity?: number | null;
  /**
   * Catalog and item reference. Holds IDs for the item and the catalog it came from, as well as further optional info.
   * This field may be empty in the case of a custom line item.
   */
  catalogReference?: CatalogReference;
  /**
   * Price of a single item.
   * @decimalValue options { gte:0 }
   */
  price?: string;
}

/** Used for grouping line items. Sent when an item is added to a cart, checkout, or order. */
export interface CatalogReference {
  /**
   * ID of the item within the catalog it belongs to.
   * @minLength 1
   * @maxLength 36
   */
  catalogItemId?: string;
  /**
   * ID of the app providing the catalog.
   *
   * You can get your app's ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).
   *
   * For items from Wix catalogs, the following values always apply:
   * + Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`
   * + Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`
   * + Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`
   * @minLength 1
   */
  appId?: string;
  /**
   * Additional item details in `key:value` pairs.
   *
   * Use this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.
   *
   * For Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)
   * or [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).
   */
  options?: Record<string, any> | null;
}

export interface TriggerToFilterBy {
  /** Custom trigger info. */
  customTrigger?: CustomTrigger;
  /**
   * Unique trigger identifier. The same value must be returned in `eligibleTriggers[i].identifier`.
   * @minLength 1
   * @maxLength 120
   */
  identifier?: string | null;
}

export interface CustomTrigger {
  /**
   * Custom trigger ID.
   * @minLength 1
   * @maxLength 100
   */
  _id?: string;
}

export interface ShippingInfo {
  /** Address (if applicable). */
  address?: Address;
  /**
   * Preferred shipping option code. For example, "usps_std_overnight".
   * @minLength 1
   * @maxLength 100
   */
  preferredShippingOptionCode?: string | null;
}

export interface Address {
  /**
   * Two-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.
   * @format COUNTRY
   */
  country?: string | null;
  /**
   * Code for a subdivision (such as state, prefecture, or province) in [ISO 3166-2](https://www.iso.org/standard/72483.html) format.
   * @maxLength 50
   */
  subdivision?: string | null;
  /**
   * City name.
   * @maxLength 50
   */
  city?: string | null;
}

export interface GetEligibleTriggersResponse {
  /**
   * List of eligible triggers. These are used by the Discount Rules API and Wix eCommerce to apply the relevant discount.
   * @maxSize 1000
   */
  eligibleTriggers?: EligibleTrigger[];
}

export interface EligibleTrigger {
  /**
   * Custom trigger ID. Must be passed with relevant `identifier`.
   * @minLength 1
   * @maxLength 100
   */
  customTriggerId?: string;
  /**
   * Unique trigger identifier. The value must be the same as its equivalent in `triggers[i].identifier` in the request payload.
   * @minLength 1
   * @maxLength 120
   */
  identifier?: string | null;
}

export interface CustomTriggerConfig {
  /**
   * Required. Base URI where the endpoints are called.
   * Wix appends the endpoint path to the base URI. For example, to call the Get Eligible Triggers endpoint at `https://my-discount-app.com/v1/getEligibleTriggers`, the base URI you provide here is `https://my-discount-app.com/`.
   */
  deploymentUri?: string;
}

/**
 * this message is not directly used by any service,
 * it exists to describe the expected parameters that SHOULD be provided to invoked Velo methods as part of open-platform.
 * e.g. SPIs, event-handlers, etc..
 * NOTE: this context object MUST be provided as the last argument in each Velo method signature.
 *
 * Example:
 * ```typescript
 * export function wixStores_onOrderCanceled({ event, metadata }: OrderCanceledEvent) {
 * ...
 * }
 * ```
 */
export interface Context {
  /** A unique identifier of the request. You may print this ID to your logs to help with future debugging and easier correlation with Wix's logs. */
  requestId?: string | null;
  /**
   * [ISO 4217](https://en.wikipedia.org/wiki/ISO_4217) 3-letter currency code.
   * @format CURRENCY
   */
  currency?: string | null;
  /** An object that describes the identity that triggered this request. */
  identity?: IdentificationData;
  /** A string representing a language and region in the format of `"xx-XX"`. First 2 letters represent the language code according to ISO 639-1. This is followed by a dash "-", and then a by 2 capital letters representing the region according to ISO 3166-2. For example, `"en-US"`. */
  languages?: string[];
  /**
   * The service provider app's instance ID.
   * @format GUID
   */
  instanceId?: string | null;
}

export enum IdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: IdentityType;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}
